import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { Expandable } from '@/components/Expandable'
import { Separator } from '@/components/Separator'
import { useTheme } from '@/hooks/useTheme'
import { currencyUtils } from '@/utils'

const { currencyConditionals } = currencyUtils

interface CryptoDescriptionProps {
  cryptoCode?: string
  cryptoName?: string
}

interface CurrencyDescriptionType {
  id: string
  key: string
  additional?: string
  description: string
  secondParagraph?: string
}

// Currency descriptions mapping to match RN version exactly
const currencyDescriptions: CurrencyDescriptionType[] = [
  {
    id: '1',
    key: 'usdt',
    additional: '(Tether)',
    description: 'currency:descriptions.usdtDescription',
  },
  {
    id: '2',
    key: 'btc',
    description: 'currency:descriptions.btcDescription',
  },
  {
    id: '3',
    key: 'eth',
    description: 'currency:descriptions.ethDescription',
  },
  {
    id: '4',
    key: 'xaut',
    description: 'currency:descriptions.xautDescription',
    secondParagraph: 'currency:descriptions.xautDescriptionSecondParagraph',
  },
  {
    id: '5',
    key: 'kwdk',
    additional: '(Dinars)',
    description: 'currency:descriptions.kwdkDescription',
  },
  {
    id: '6',
    key: 'aedk',
    additional: '(Emirati Dirham)',
    description: 'currency:descriptions.aedkDescription',
  },
  {
    id: '7',
    key: 'sark',
    additional: '(Saudi Riyal)',
    description: 'currency:descriptions.sarkDescription',
  },
  {
    id: '8',
    key: 'sol',
    additional: 'Solana',
    description: 'currency:descriptions.solDescription',
  },
]

const normalizeText = (text: string) => text.toLowerCase().trim()

const getCurrencyDescription = (code: string) => {
  const codeNormalized = normalizeText(code)
  return currencyDescriptions.find(
    ({ key }) => normalizeText(key) === codeNormalized,
  )
}

// Constants matching RN exactly
const FONT_SIZE = 16
const LINE_HEIGHT = FONT_SIZE * 1.5 // 24px
const INITIAL_EXPANDABLE_HEIGHT = LINE_HEIGHT * 2 // 48px - exactly 2 lines

export const CryptoDescription: React.FC<CryptoDescriptionProps> = ({
  cryptoCode = '',
}) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const theme = useTheme()

  const { isStablecoin } = currencyConditionals(cryptoCode)
  const currencyDescription = getCurrencyDescription(cryptoCode)

  const canRenderDescription = !!currencyDescription && cryptoCode

  if (!canRenderDescription) return null

  return (
    <>
      {!isStablecoin && (
        <div style={{ marginTop: 12, marginBottom: 12 }}>
          <Separator />
        </div>
      )}
      <BaseView gap={12}>
        <Expandable
          expanded={expanded}
          initialHeight={INITIAL_EXPANDABLE_HEIGHT}
          showTail
        >
          <BaseText
            key={String(expanded)}
            fontSize={FONT_SIZE}
            fontFamily="medium"
            color="label.description"
            style={{
              lineHeight: `${LINE_HEIGHT}px`,
              whiteSpace: 'pre-line',
            }}
          >
            {t(currencyDescription.description)}
            {!!currencyDescription?.secondParagraph && (
              <>
                {'\n'}
                {'\n'}
                {t(currencyDescription.secondParagraph)}
              </>
            )}
          </BaseText>
        </Expandable>

        <BaseText
          fontSize={16}
          fontFamily="semibold"
          style={{
            color: theme.colors.label.clickable,
            cursor: 'pointer',
            alignSelf: 'flex-start',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? t('common:showLess') : t('common:showMore')}
        </BaseText>
      </BaseView>
      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <Separator />
      </div>
    </>
  )
}
